import { createAudioContext, playPcmBase64 } from './audio';
import type { MessageChunk } from './chat/types';
import { BlendshapeAnimator } from './blendshapeAnimator';

/**
 * Manages sequential playback of message chunks from WebSocket responses
 *
 * Handles WebSocket response chunks that arrive as partial data with sequence numbers.
 * Sequence numbers are per-request/session (not reset between messages).
 *
 * Example:
 * - Request 1: sequences 0, 1, 2
 * - Request 2: sequences 3, 4, 5 (continues from previous)
 * - Request 3: sequences 6, 7, 8 (continues from previous)
 *
 * Chunks are queued and played sequentially, waiting for missing sequences if needed.
 */
export class SequentialResponsePlayer {
  private queue: MessageChunk[] = [];
  private isPlaying = false;
  private currentPlayingSequence = 0; // Track which sequence is currently playing (global, continuous)
  private currentRequestId: string | null = null; // Track which request is currently playing
  private activeChunk: MessageChunk | null = null;
  // One context per answer: opening one per chunk cost about 40 ms between chunks.
  private audioContext: AudioContext | null = null;
  private blendshapeAnimator: BlendshapeAnimator | null = null;
  private checkInterval: number | null = null;
  private readonly CHECK_INTERVAL_MS = 50; // Check every 50ms if current playback is done
  private idleWaiters: Array<() => void> = [];

  constructor(blendshapeAnimator: BlendshapeAnimator | null = null) {
    this.blendshapeAnimator = blendshapeAnimator;
  }

  /**
   * Open a fresh audio context for the next answer. Call from a user gesture: a context first
   * touched by a WebSocket message stays suspended, and its resume() promise then never settles.
   * Fresh per answer because Safari keeps a context that sat silent "running" while its output
   * stays mute.
   */
  unlockAudio() {
    void this.audioContext?.close();
    const context = this.audioContext = createAudioContext();
    if (context.state === 'suspended') void context.resume();
  }

  /**
   * Enqueue a message chunk for sequential playback
   *
   * This is called for each response chunk from the WebSocket.
   * Chunks are automatically sorted by sequence number.
   * Sequence numbers are continuous across requests (not reset to 0 for each message).
   */
  enqueueChunk(chunk: MessageChunk) {
    // Insert in sequence order (maintain sorted queue)
    let inserted = false;
    for (let i = 0; i < this.queue.length; i++) {
      if (this.queue[i].sequence > chunk.sequence) {
        this.queue.splice(i, 0, chunk);
        inserted = true;
        break;
      }
    }
    if (!inserted) {
      this.queue.push(chunk);
    }

    // Try to process queue
    this.processQueue();
  }

  /**
   * Process the queue - play next chunk in sequence order
   *
   * Only plays chunks in sequence order (continuous, not reset per request).
   * Sequence: 0, 1, 2, 3, 4, 5... (continues across multiple requests)
   *
   * If chunks arrive out of order, waits for the next expected sequence.
   */
  private processQueue() {
    // Don't process if currently playing
    if (this.isPlaying) {
      console.warn('[SequentialResponsePlayer] Already playing, skipping queue processing');
      return;
    }

    // Don't process if queue is empty
    if (this.queue.length === 0) {
      console.warn('[SequentialResponsePlayer] Queue is empty, skipping queue processing');
      return;
    }

    // Find the next chunk in sequence order
    // The next chunk should have sequence = currentPlayingSequence + 1
    const nextExpectedSequence = this.currentPlayingSequence + 1;
    const nextChunkIndex = this.queue.findIndex(chunk => chunk.sequence === nextExpectedSequence);

    // If the next expected chunk isn't available, wait for it
    if (nextChunkIndex === -1) {
      console.warn('[SequentialResponsePlayer] Waiting for chunk with sequence', nextExpectedSequence);
      return;
    }

    // Remove the chunk from queue and play it
    const chunk = this.queue.splice(nextChunkIndex, 1)[0];
    this.currentPlayingSequence = chunk.sequence;
    this.currentRequestId = chunk.request_id;
    this.playChunk(chunk);
  }

  /**
   * Play a single message chunk
   * Starts audio and animation simultaneously, then waits for both to complete
   */
  private playChunk(chunk: MessageChunk) {
    console.log('[SequentialResponsePlayer] Playing chunk:', chunk);
    this.isPlaying = true;
    this.activeChunk = chunk;
    const hasAudio = !!chunk.voice_data.data;
    const hasAnimation = this.blendshapeAnimator && chunk.blendshapes && chunk.blendshapes && chunk.blendshapes.length > 0;
    console.log('[SequentialResponsePlayer] Has animation:', hasAnimation);

    // Calculate expected duration in milliseconds
    let expectedDurationMs = 0;

    if (hasAudio) {
      expectedDurationMs = chunk.voice_data.duration_ms || 0;
    }

    if (hasAnimation && !hasAudio) {
      // If only animation (no audio), calculate from frame count
      expectedDurationMs = (chunk.blendshapes.length * 1000) / chunk.frame_rate;
    }

    // Start audio playback
    if (hasAudio && chunk.voice_data.data) {
      // A stale end from a chunk that clear() abandoned must not finish the chunk playing now.
      const finishThisChunk = () => {
        if (this.activeChunk === chunk) this.finishCurrentPlayback();
      };
      const context = this.audioContext ??= createAudioContext();
      playPcmBase64(context, chunk.voice_data.data, {
        sampleRate: chunk.voice_data.sample_rate,
        channels: chunk.voice_data.channels,
        bitDepth: chunk.voice_data.bit_depth,
      }).then(({ source }) => {
        // A timer started before the audio could start the next chunk while this one still
        // plays, and two full-scale chunks summed clip. A context the browser keeps suspended
        // never ends, so fall back to the duration there rather than wait forever.
        if (context.state === 'running') {
          source.addEventListener('ended', finishThisChunk);
        } else {
          this.checkInterval = window.setInterval(finishThisChunk, expectedDurationMs);
        }
      }).catch(err => {
        console.error('[SequentialResponsePlayer] Audio playback error:', err);
        finishThisChunk();
      });
    }

    // Start animation
    if (hasAnimation && this.blendshapeAnimator) {
      this.blendshapeAnimator.setFrames(chunk.blendshapes, chunk.frame_rate);
      this.blendshapeAnimator.play();
    }

    if (!hasAnimation && !hasAudio) {
      console.warn('[SequentialResponsePlayer] No audio or animation data, skipping playback');
      this.finishCurrentPlayback();
      return;
    }

    if (hasAudio) return;

    // Schedule check to see when playback finishes
    this.checkInterval = window.setInterval(() => {
      this.finishCurrentPlayback();
    }, expectedDurationMs);
  }

  /**
   * Finish current playback and process next in queue
   */
  private finishCurrentPlayback() {
    if (this.checkInterval !== null) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.isPlaying = false;
    this.activeChunk = null;

    // Stop any ongoing animation
    if (this.blendshapeAnimator) {
      this.blendshapeAnimator.stop();
    }

    // Process next chunk in queue
    this.processQueue();
    this.notifyIfIdle();
  }

  /** Resolves once every queued chunk has finished playing, or at once when nothing is queued or playing. */
  whenIdle(): Promise<void> {
    if (this.isIdle()) return Promise.resolve();
    return new Promise((resolve) => this.idleWaiters.push(resolve));
  }

  private isIdle(): boolean {
    return !this.isPlaying && this.queue.length === 0;
  }

  private notifyIfIdle() {
    if (!this.isIdle()) return;
    const waiters = this.idleWaiters;
    this.idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  /**
   * Reset for a new message: clear queue, stop playback, and reset sequence counter to 0
   */
  reset() {
    this.clear();
    this.currentPlayingSequence = 0;
  }

  /**
   * Clear the queue and stop playback
   * Note: Does NOT reset currentPlayingSequence since sequence is continuous per session
   */
  clear() {
    this.queue = [];
    if (this.checkInterval !== null) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.isPlaying = false;
    this.activeChunk = null;
    this.currentRequestId = null;
    if (this.blendshapeAnimator) {
      this.blendshapeAnimator.stop();
    }
    this.notifyIfIdle();
  }

  /**
   * Dispose resources
   */
  dispose() {
    this.clear();
    void this.audioContext?.close();
    this.audioContext = null;
    this.blendshapeAnimator = null;
  }
}

